import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import { useAppStore } from '../../shared/stores/useAppStore';
import snakeToPascal from '../../tools';
import noDetails from '../../components/HistoryRowsDetails/noDetails';
import applyPresetDetails from '../../components/HistoryRowsDetails/applyPreset';
import scenarioCreateDetails from '../../components/HistoryRowsDetails/scenarioCreate';
import scenarioRenameDetails from '../../components/HistoryRowsDetails/renameScenario';
import modelDataCreateDetails from '../../components/HistoryRowsDetails/createModelData';
import modelDataRenameDetails from '../../components/HistoryRowsDetails/renameModelData';
import modelDataDeleteDetails from '../../components/HistoryRowsDetails/deleteModelData';
import modelDataValueUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueStd';
import modelDataValueUpdateLstAddDetails from '../../components/HistoryRowsDetails/updateModelDataValueListAdd';
import modelDataValueUpdateLstDeleteDetails from '../../components/HistoryRowsDetails/updateModelDataValueListDelete';
import modelDataValueUpdateLstUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueListUpdate';
import modelDataValueRelationUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueRelation';
import modelDataValueUpdateTsTimingDetails from '../../components/HistoryRowsDetails/updateModelDataValueTsTiming';
import modelDataValueTimingRefUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueTimingReference';
import modelDataValueTimeSerieElemUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueTimeSerieElem';
import modelDataValueUpdateDateDetails from '../../components/HistoryRowsDetails/updateModelDataValueDate';
import modelDataValueTimeSerieUpdateDetails from '../../components/HistoryRowsDetails/updateModelDataValueTimeSerie';
import modelDataCopyDetails from '../../components/HistoryRowsDetails/copyModelData';
import { cn } from '../../lib/utils';
import { useScenarioHistory } from '../../shared/hooks/queries/useHistory';

interface ActionTypeConfig {
    name: string;
    icon: string;
    component: React.ComponentType<any>;
}

const DISPLAY_BY_TYPE: Record<string, ActionTypeConfig> = {
    'model_create': { name: 'Created model', icon: 'add', component: noDetails },
    'model_create_apply_preset': { name: 'Applied preset', icon: 'tune', component: applyPresetDetails },
    'scenario_create': { name: 'Created scenario', icon: 'add', component: scenarioCreateDetails },
    'scenario_rename': { name: 'Renamed scenario', icon: 'edit', component: scenarioRenameDetails },
    'scenario_delete': { name: 'Deleted scenario', icon: 'delete', component: noDetails },
    'scenario_create_modeldata': { name: 'Created', icon: 'add', component: modelDataCreateDetails },
    'scenario_copy_modeldata': { name: 'Copied', icon: 'content_copy', component: modelDataCopyDetails },
    'scenario_rename_modeldata': { name: 'Renamed', icon: 'edit', component: modelDataRenameDetails },
    'scenario_delete_modeldata': { name: 'Deleted', icon: 'delete', component: modelDataDeleteDetails },
    'scenario_update_modeldata_value_bool': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_date': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDateDetails },
    'scenario_update_modeldata_value_enum': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_float': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_integer': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_str': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_lst_elem_add': { name: 'Added list item to', icon: 'playlist_add', component: modelDataValueUpdateLstAddDetails },
    'scenario_update_modeldata_value_lst_elem_update': { name: 'Updated list item in', icon: 'edit', component: modelDataValueUpdateLstUpdateDetails },
    'scenario_update_modeldata_value_lst_elem_delete': { name: 'Removed list item from', icon: 'playlist_remove', component: modelDataValueUpdateLstDeleteDetails },
    'scenario_update_modeldata_value_relation': { name: 'Updated relation in', icon: 'link', component: modelDataValueRelationUpdateDetails },
    'scenario_update_modeldata_value_ts_timing': { name: 'Updated timing in', icon: 'schedule', component: modelDataValueUpdateTsTimingDetails },
    'scenario_update_modeldata_value_time_serie': { name: 'Updated series in', icon: 'show_chart', component: modelDataValueTimeSerieUpdateDetails },
    'scenario_update_modeldata_value_time_serie_elem': { name: 'Updated series value in', icon: 'timeline', component: modelDataValueTimeSerieElemUpdateDetails },
    'scenario_update_modeldata_value_timing_reference': { name: 'Updated timing ref in', icon: 'event', component: modelDataValueTimingRefUpdateDetails },
    'scenario_update_modeldata_value_customizable_timing_reference': { name: 'Updated timing ref in', icon: 'event', component: modelDataValueTimingRefUpdateDetails },
};

const UTCOffset = new Date().getTimezoneOffset() * -1;

// Group rows by date (sorted newest first)
const groupByDate = (rows: any[]) => {
    // Sort rows by date descending (newest first)
    const sortedRows = [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const groups: Record<string, any[]> = {};
    sortedRows.forEach(row => {
        const date = new Date(row.date);
        const dateKey = date.toDateString();
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(row);
    });

    // Sort groups by date descending (newest first)
    return Object.entries(groups)
        .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
        .map(([date, items]) => ({ date, items }));
};

export default function History() {
    const { model, currentScenario, currentSensitivity, currentEntity } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }
    return <HistoryContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} />;
}

// Date range presets
const DATE_PRESETS = {
    all: { label: 'All time', days: null },
    today: { label: 'Today', days: 0 },
    week: { label: 'Last 7 days', days: 7 },
    month: { label: 'Last 30 days', days: 30 },
};

// Action type categories for filtering
const ACTION_CATEGORIES = {
    all: { label: 'All actions', types: null },
    created: {
        label: 'Created',
        types: ['model_create', 'scenario_create', 'scenario_create_modeldata', 'scenario_copy_modeldata', 'scenario_update_modeldata_value_lst_elem_add']
    },
    updated: {
        label: 'Updated',
        types: ['model_create_apply_preset', 'scenario_rename', 'scenario_rename_modeldata',
                'scenario_update_modeldata_value_bool', 'scenario_update_modeldata_value_date',
                'scenario_update_modeldata_value_enum', 'scenario_update_modeldata_value_float',
                'scenario_update_modeldata_value_integer', 'scenario_update_modeldata_value_str',
                'scenario_update_modeldata_value_lst_elem_update', 'scenario_update_modeldata_value_relation',
                'scenario_update_modeldata_value_ts_timing', 'scenario_update_modeldata_value_time_serie',
                'scenario_update_modeldata_value_time_serie_elem', 'scenario_update_modeldata_value_timing_reference',
                'scenario_update_modeldata_value_customizable_timing_reference']
    },
    deleted: {
        label: 'Deleted',
        types: ['scenario_delete', 'scenario_delete_modeldata', 'scenario_update_modeldata_value_lst_elem_delete']
    },
};

function HistoryContent({ currentEntity, model, currentScenario, currentSensitivity }) {
    const userSettings = useAppStore(s => s.userSettings);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // Navigation state
    const [searchQuery, setSearchQuery] = useState("");
    const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
    const [actionFilter, setActionFilter] = useState<'all' | 'created' | 'updated' | 'deleted'>('all');
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

    const { data: tableRows = [], isLoading: loading } = useScenarioHistory(
        model?.id ?? 0,
        currentScenario?.id ?? 0,
    );

    // Filter rows based on search, date, and action type
    const filteredRows = useMemo(() => {
        let rows = [...tableRows];

        // Filter by date range
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const preset = DATE_PRESETS[dateFilter];

            if (preset.days === 0) {
                // Today only
                rows = rows.filter(row => {
                    const rowDate = new Date(row.date);
                    return rowDate >= today;
                });
            } else if (preset.days) {
                const cutoff = new Date(today);
                cutoff.setDate(cutoff.getDate() - preset.days);
                rows = rows.filter(row => new Date(row.date) >= cutoff);
            }
        }

        // Filter by action type
        if (actionFilter !== 'all') {
            const allowedTypes = ACTION_CATEGORIES[actionFilter]?.types;
            if (allowedTypes) {
                rows = rows.filter(row => allowedTypes.includes(row.type));
            }
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            rows = rows.filter(row => {
                const objectName = row.model_data_name || row.name || row.scenario_name || '';
                const userName = row.user_name || '';
                const typeName = DISPLAY_BY_TYPE[row.type]?.name || '';
                return objectName.toLowerCase().includes(query) ||
                       userName.toLowerCase().includes(query) ||
                       typeName.toLowerCase().includes(query);
            });
        }

        return rows;
    }, [tableRows, searchQuery, dateFilter, actionFilter]);

    const groupedRows = useMemo(() => groupByDate(filteredRows), [filteredRows]);


    const toggleDateGroup = (date: string) => {
        setExpandedDates(prev => {
            const newSet = new Set(prev);
            newSet.has(date) ? newSet.delete(date) : newSet.add(date);
            return newSet;
        });
    };

    const expandAllDates = () => setExpandedDates(new Set(groupedRows.map(g => g.date)));
    const collapseAllDates = () => setExpandedDates(new Set());

    const hasActiveFilters = searchQuery || dateFilter !== 'all' || actionFilter !== 'all';

    const getCurrentTzDate = (dateStr) => new Date(new Date(dateStr).getTime() + UTCOffset * 60000);
    const formatTime = (date) => date.toLocaleTimeString(userSettings.dateSystem === 'DMY' ? 'en-GB' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    const formatDateHeader = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString(userSettings.dateSystem === 'DMY' ? 'en-GB' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const getModelDataName = (row) => {
        if (row.type === 'scenario_rename_modeldata') return row.new_value;
        if (row.type === 'scenario_create_modeldata') return row.name;
        return row.model_data_name;
    };

    const toggleRow = (rowId: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId);
            return newSet;
        });
    };

    return (
        <div style={{ height: "100%", overflow: "auto" }}>
            <div className="flex flex-col h-full">
                <FMEPageHeader
                    title="History"
                    breadcrumb={[model.name]}
                    description={loading ? "Loading..." : `${tableRows.length} changes recorded.`}
                    actions={<ScenarioSensitivitySelector />}
                />
                <motion.div
                    className="px-8 py-6 flex-1 overflow-y-auto"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.16 } }}
                >
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500 text-sm">Loading history...</p>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-4xl">
                        {/* Filter Bar */}
                        <div className="mb-6 space-y-4">
                            {/* Primary filters row */}
                            <div className="flex items-center gap-4 flex-wrap">
                                {/* Search */}
                                <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xs">
                                    <div className="relative flex-1">
                                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search by name or user..."
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery('')}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">close</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Date range filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Period:</span>
                                {Object.entries(DATE_PRESETS).map(([key, { label }]) => (
                                    <button
                                        key={key}
                                        onClick={() => setDateFilter(key as any)}
                                        className={cn(
                                            "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                                            dateFilter === key
                                                ? "bg-primary text-white"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* Action type filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Action:</span>
                                {Object.entries(ACTION_CATEGORIES).map(([key, { label }]) => (
                                    <button
                                        key={key}
                                        onClick={() => setActionFilter(key as any)}
                                        className={cn(
                                            "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                                            actionFilter === key
                                                ? "bg-primary text-white"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* Results summary & actions */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                    <span>{filteredRows.length} changes{hasActiveFilters && ` (filtered from ${tableRows.length})`}</span>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={() => { setSearchQuery(''); setDateFilter('all'); setActionFilter('all'); }}
                                            className="text-primary hover:underline text-xs"
                                        >
                                            Clear all filters
                                        </button>
                                    )}
                                </div>
                                {groupedRows.length > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={expandAllDates}
                                            className="text-xs text-slate-500 hover:text-slate-700"
                                        >
                                            Expand all
                                        </button>
                                        <span className="text-slate-300">|</span>
                                        <button
                                            onClick={collapseAllDates}
                                            className="text-xs text-slate-500 hover:text-slate-700"
                                        >
                                            Collapse all
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Timeline */}
                        {groupedRows.length > 0 ? (
                            <div className="space-y-2">
                                {groupedRows.map(group => {
                                    const isDateExpanded = expandedDates.has(group.date);
                                    return (
                                    <div key={group.date} className="border border-slate-200 rounded-lg overflow-hidden">
                                        {/* Date Header - Clickable */}
                                        <button
                                            onClick={() => toggleDateGroup(group.date)}
                                            className={cn(
                                                "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                                                isDateExpanded ? "bg-slate-50" : "bg-white hover:bg-slate-50"
                                            )}
                                        >
                                            <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <span className="material-symbols-outlined text-primary text-[16px]">calendar_today</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-semibold text-slate-900">{formatDateHeader(group.date)}</h3>
                                            </div>
                                            <span className="text-xs text-slate-400 tabular-nums">{group.items.length} changes</span>
                                            <span className={cn(
                                                "material-symbols-outlined text-[18px] text-slate-400 transition-transform",
                                                isDateExpanded && "rotate-180"
                                            )}>expand_more</span>
                                        </button>

                                        {/* Day's Events - Collapsible */}
                                        {isDateExpanded && (
                                        <div className="border-t border-slate-100 bg-white">
                                        <div className="ml-8 border-l-2 border-slate-100 pl-6 py-2 space-y-1">
                                            {group.items.map((row: any) => {
                                                const config = DISPLAY_BY_TYPE[row.type];
                                                const isExpanded = expandedRows.has(row.id);
                                                const objectName = getModelDataName(row);
                                                const objectType = row.model_data_type ? snakeToPascal(row.model_data_type) : null;

                                                return (
                                                    <div key={row.id} className="group">
                                                        <button
                                                            onClick={() => toggleRow(row.id)}
                                                            className={cn(
                                                                "w-full text-left p-3 rounded-lg transition-all",
                                                                isExpanded ? "bg-slate-50" : "hover:bg-slate-50"
                                                            )}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                {/* Timeline dot */}
                                                                <div className="relative -ml-[33px] mt-1.5">
                                                                    <div className="size-2.5 rounded-full bg-slate-300 group-hover:bg-primary transition-colors" />
                                                                </div>

                                                                {/* Content */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-xs text-slate-400 tabular-nums w-12">
                                                                            {formatTime(getCurrentTzDate(row.date))}
                                                                        </span>
                                                                        <span className="text-sm text-slate-600">
                                                                            <span className="font-medium text-slate-900">{row.user_name}</span>
                                                                            {' '}{config?.name || 'modified'}
                                                                            {objectType && (
                                                                                <span className="text-slate-400"> {objectType}</span>
                                                                            )}
                                                                            {objectName && (
                                                                                <span className="font-medium text-slate-900"> "{objectName}"</span>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Expand indicator */}
                                                                <span className={cn(
                                                                    "material-symbols-outlined text-[18px] text-slate-400 transition-transform",
                                                                    isExpanded && "rotate-180"
                                                                )}>expand_more</span>
                                                            </div>
                                                        </button>

                                                        {/* Expanded Details */}
                                                        {isExpanded && config && (
                                                            <div className="ml-8 mr-3 mb-3">
                                                                <config.component row={row} />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        ) : tableRows.length > 0 ? (
                            <div className="text-center py-20">
                                <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                    <span className="material-symbols-outlined text-[28px] text-slate-400">filter_list_off</span>
                                </div>
                                <h3 className="text-lg font-medium text-slate-900 mb-1">No matching results</h3>
                                <p className="text-sm text-slate-500 mb-4">Try adjusting your search or filters</p>
                                <button
                                    onClick={() => { setSearchQuery(''); setDateFilter('all'); setActionFilter('all'); }}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[16px]">refresh</span>
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-20">
                                <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                    <span className="material-symbols-outlined text-[28px] text-slate-400">history</span>
                                </div>
                                <h3 className="text-lg font-medium text-slate-900 mb-1">No activity yet</h3>
                                <p className="text-sm text-slate-500">Changes to this scenario will appear here</p>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
            </div>
        </div>
    );
}

export { DISPLAY_BY_TYPE, UTCOffset };
